import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ChartModule } from 'angular-highcharts';
import * as Highcharts from 'highcharts';
import noData from 'highcharts/modules/no-data-to-display';
import { TooltipModule } from 'ng2-tooltip-directive';
import { PinchZoomModule } from 'ngx-pinch-zoom';
import { MaterialModule } from '../material/material.module';
import { AbsPipe } from './abs.pipe';
import { AddMinutesPipe } from './add-minutes.pipe';
import { AddressSearchComponent } from './address-search/address-search.component';
import { AgoPipe } from './ago.pipe';
import { AssetNamePipe } from './asset-name.pipe';
import { AssetStatusPipe } from './asset-status.pipe';
import { BoxTechnologiesNamePipe } from './box-technologies-name.pipe';
import { CeilPipe } from './ceil.pipe';
import { ChargeTypePipe } from './charge-type.pipe';
import { CommitmentCalendarComponent } from './commitment-calendar/commitment-calendar.component';
import { CompareWithPipe } from './compare-with.pipe';
import { ConditionalSlicePipe } from './conditional-slice.pipe';
import { ConfirmActionDialogComponent } from './confirm-action-dialog/confirm-action-dialog.component';
import { ContractStatusPipe } from './contract-status.pipe';
import { CountdownPipe } from './countdown.pipe';
import { CurrencyInPenniesPipe } from './currency-in-pennies.pipe';
import { DashIfNothingPipe } from './dash-if-nothing.pipe';
import { EarningsChartComponent } from './earnings-chart/earnings-chart.component';
import { EarningsProgramMemberDirective } from './earnings-program-member.directive';
import { ExcludeFirstAndLastPipe } from './exclude-first-and-last.pipe';
import { FeatureFlagDirective } from './feature-flag.directive';
import { FromNowPipe } from './from-now.pipe';
import { H2021AnnounceDirective } from './h2021-announce.directive';
import { HolidayTwentyoneCountdownComponent } from './holiday-twentyone-countdown/holiday-twentyone-countdown.component';
import { HourOfDayPipe } from './hour-of-day.pipe';
import { HseScoreFromIdPipe } from './hse-score-from-id.pipe';
import { ImageLightboxComponent } from './image-lightbox/image-lightbox.component';
import { ImagePreviewComponent } from './image-preview/image-preview.component';
import { InProgressLoadByPhoneV2Pipe } from './in-progress-load-v2.pipe';
import { InThePastPipe } from './in-the-past.pipe';
import { InternalDirective } from './internal.directive';
import { IsAdminDirective } from './is-admin.directive';
import { IsDriverDirective } from './is-driver.directive';
import { IsFiveFTourDispatcherDirective } from './is-fivef-tour-dispatcher.directive';
import { IsImagePipe } from './is-image.pipe';
import { IsNotOnlyFiveFTourOwnerDirective } from './is-not-only-five-f-tour-owner.directive';
import { JoinWithAndPipe } from './join-with-and.pipe';
import { KpiDisplayDurationPipe } from './kpi-display-duration.pipe';
import { LamAssetStatusPipe } from './lam-asset-status.pipe';
import { LamStatusPipe } from './lam-status.pipe';
import { LastElementPipe } from './last-element.pipe';
import { LastLocationTypePipe } from './last-location-type.pipe';
import { LetteringPipe } from './lettering.pipe';
import { LoaderDialogComponent } from './loader-dialog/loader-dialog.component';
import { LoadsChartComponent } from './loads-chart/loads-chart.component';
import { MapPipe } from './map.pipe';
import { MaxPipe } from './max.pipe';
import { MinutesToHumanPipe } from './minutes-to-human.pipe';
import { CurrencyMatInputInputDirective } from './money-mat-input.directive';
import { CurrencyInputDirective } from './money.directive';
import { MonthNamePipe } from './month-name.pipe';
import { NumberBadgeComponent } from './number-badge/number-badge.component';
import { NumberInputDirective } from './number-input.directive';
import { OperationalPipe } from './operational.pipe';
import { OrderByPipe } from './order-by.pipe';
import { OrdinalPipe } from './ordinal.pipe';
import { PageTitleDirective } from './page-title.directive';
import { PayMerchantDirective } from './pay-merchant.directive';
import { PaymentIssueDialogComponent } from './payment-issue-dialog/payment-issue-dialog.component';
import { PhonePipe } from './phone.pipe';
import { PieChartComponent } from './pie-chart/pie-chart.component';
import { PlusMorePipe } from './plus-more.pipe';
import { PrettyPrintHoursPipe } from './pretty-print-hours.pipe';
import { PrettyPrintMinutesPipe } from './pretty-print-minutes.pipe';
import { PrettyPrintSecondsPipe } from './pretty-print-seconds.pipe';
import { PushPipe } from './push.pipe';
import { RelationshipDirective } from './relationship.directive';
import { RemoveAriaOwnsDirective } from './remove-aria-owns.directive';
import { SafetyTypePipe } from './safety-type.pipe';
import { ShIntakeDriverStatusPipe } from './sh-intake-driver-status.pipe';
import { ShiftChartV2Component } from './shift-chart-v2/shift-chart-v2.component';
import { ShiftChartComponent } from './shift-chart/shift-chart.component';
import { ShiftMapComponent } from './shift-map/shift-map.component';
import { ShortNamePipe } from './shortName.pipe';
import { SortIndicatorComponent } from './sort-indicator/sort-indicator.component';
import { StandardDateOnlyPipe } from './standard-date-only.pipe';
import { StandardDatePipe } from './standard-date.pipe';
import { StandardTimePipe } from './standard-time.pipe';
import { TaskTypeImagePipe } from './task-type-image.pipe';
import { TaskTypePipe } from './task-type.pipe';
import { TierV2ColorPipe } from './tier-v2-color.pipe';
import { TimeInTimezonePipe } from './time-in-timezone.pipe';
import { TourLoadStatusPipe } from './tour-load-status.pipe';
import { ToursTrailerTypePipe } from './tours-trailer-type.pipe';
import { TrackByKey2Pipe } from './track-by-key-2.pipe';
import { TrackByKey } from './track-by.pipe';
import { TrianglePipe } from './triangle.pipe';
import { UserFromPhonePipe } from './user-from-phone.pipe';

const tooltipOptions = {
  'tooltip-class': 'tooltip-overrides',
};

noData(Highcharts);

@NgModule({
  declarations: [
    TrackByKey,
    StandardDatePipe,
    InProgressLoadByPhoneV2Pipe,
    PhonePipe,
    FeatureFlagDirective,
    DashIfNothingPipe,
    ImageLightboxComponent,
    ImagePreviewComponent,
    NumberInputDirective,
    OperationalPipe,
    LoadsChartComponent,
    NumberBadgeComponent,
    CurrencyInputDirective,
    EarningsChartComponent,
    PrettyPrintMinutesPipe,
    CurrencyInPenniesPipe,
    PieChartComponent,
    AgoPipe,
    SortIndicatorComponent,
    AbsPipe,
    FromNowPipe,
    PageTitleDirective,
    MaxPipe,
    ConditionalSlicePipe,
    OperationalPipe,
    ShiftChartComponent,
    ShiftChartV2Component,
    CommitmentCalendarComponent,
    MonthNamePipe,
    JoinWithAndPipe,
    ShiftMapComponent,
    StandardTimePipe,
    InThePastPipe,
    StandardDateOnlyPipe,
    AddMinutesPipe,
    PrettyPrintSecondsPipe,
    OrdinalPipe,
    UserFromPhonePipe,
    TrianglePipe,
    ChargeTypePipe,
    EarningsProgramMemberDirective,
    RelationshipDirective,
    CeilPipe,
    HourOfDayPipe,
    LoaderDialogComponent,
    ConfirmActionDialogComponent,
    ShortNamePipe,
    LamStatusPipe,
    TaskTypePipe,
    TaskTypeImagePipe,
    CountdownPipe,
    HseScoreFromIdPipe,
    SafetyTypePipe,
    OrderByPipe,
    ExcludeFirstAndLastPipe,
    LastElementPipe,
    LetteringPipe,
    PlusMorePipe,
    ContractStatusPipe,
    AssetStatusPipe,
    ShIntakeDriverStatusPipe,
    InternalDirective,
    AssetNamePipe,
    BoxTechnologiesNamePipe,
    PayMerchantDirective,
    LastLocationTypePipe,
    IsAdminDirective,
    MinutesToHumanPipe,
    TrackByKey2Pipe,
    MapPipe,
    IsImagePipe,
    CurrencyMatInputInputDirective,
    IsFiveFTourDispatcherDirective,
    IsDriverDirective,
    PrettyPrintHoursPipe,
    H2021AnnounceDirective,
    HolidayTwentyoneCountdownComponent,
    IsNotOnlyFiveFTourOwnerDirective,
    CompareWithPipe,
    PushPipe,
    TourLoadStatusPipe,
    TimeInTimezonePipe,
    AddressSearchComponent,
    ToursTrailerTypePipe,
    TierV2ColorPipe,
    RemoveAriaOwnsDirective,
    LamAssetStatusPipe,
    KpiDisplayDurationPipe,
    PaymentIssueDialogComponent,
  ],
  imports: [
    CommonModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ChartModule,
    TooltipModule.forRoot(tooltipOptions),
    PinchZoomModule,
  ],
  exports: [
    TrackByKey,
    StandardDatePipe,
    InProgressLoadByPhoneV2Pipe,
    PhonePipe,
    FeatureFlagDirective,
    DashIfNothingPipe,
    OperationalPipe,
    EarningsChartComponent,
    NumberInputDirective,
    NumberBadgeComponent,
    PrettyPrintMinutesPipe,
    AgoPipe,
    FromNowPipe,
    PieChartComponent,
    AbsPipe,
    PageTitleDirective,
    LoadsChartComponent,
    CurrencyInPenniesPipe,
    SortIndicatorComponent,
    ChartModule,
    CurrencyInputDirective,
    RouterModule,
    TooltipModule,
    MaxPipe,
    ConditionalSlicePipe,
    ShiftChartComponent,
    ShiftChartV2Component,
    CommitmentCalendarComponent,
    MonthNamePipe,
    JoinWithAndPipe,
    ShiftMapComponent,
    StandardTimePipe,
    InThePastPipe,
    StandardDateOnlyPipe,
    AddMinutesPipe,
    PrettyPrintSecondsPipe,
    OrdinalPipe,
    UserFromPhonePipe,
    TrianglePipe,
    ChargeTypePipe,
    EarningsProgramMemberDirective,
    RelationshipDirective,
    CeilPipe,
    HourOfDayPipe,
    ShortNamePipe,
    LamStatusPipe,
    TaskTypePipe,
    TaskTypeImagePipe,
    CountdownPipe,
    HseScoreFromIdPipe,
    SafetyTypePipe,
    OrderByPipe,
    ExcludeFirstAndLastPipe,
    LastElementPipe,
    LetteringPipe,
    PlusMorePipe,
    ContractStatusPipe,
    AssetStatusPipe,
    ShIntakeDriverStatusPipe,
    InternalDirective,
    AssetNamePipe,
    BoxTechnologiesNamePipe,
    PayMerchantDirective,
    LastLocationTypePipe,
    IsAdminDirective,
    MinutesToHumanPipe,
    TrackByKey2Pipe,
    MapPipe,
    IsImagePipe,
    CurrencyMatInputInputDirective,
    IsFiveFTourDispatcherDirective,
    IsDriverDirective,
    PrettyPrintHoursPipe,
    H2021AnnounceDirective,
    HolidayTwentyoneCountdownComponent,
    IsNotOnlyFiveFTourOwnerDirective,
    CompareWithPipe,
    PushPipe,
    TourLoadStatusPipe,
    TimeInTimezonePipe,
    AddressSearchComponent,
    ToursTrailerTypePipe,
    TierV2ColorPipe,
    RemoveAriaOwnsDirective,
    LamAssetStatusPipe,
    KpiDisplayDurationPipe,
    PaymentIssueDialogComponent,
  ],
  providers: [CurrencyInPenniesPipe, AssetStatusPipe, OrderByPipe, TierV2ColorPipe, LamAssetStatusPipe],
})
export class SharedModule {}
